import { Injectable } from '@angular/core';
import type {
  PDFDocumentLoadingTask,
  PDFDocumentProxy,
  PDFWorker,
  RenderTask,
} from 'pdfjs-dist';

const MAX_DEVICE_PIXEL_RATIO = 2;

export interface QrPosterPdfPreviewRender {
  readonly completed: Promise<void>;
  dispose(): void;
}

type PdfJsModule = typeof import('pdfjs-dist');

@Injectable({ providedIn: 'root' })
export class QrPosterPdfPreviewService {
  private pdfJsModulePromise: Promise<PdfJsModule> | null = null;
  private pdfJsWorkerPromise: Promise<PDFWorker> | null = null;
  private pdfJsWorker: PDFWorker | null = null;

  preload(): Promise<void> {
    return this.getPdfJsModule().then(() => undefined);
  }

  prepareWorker(): Promise<void> {
    return this.getOrCreatePdfJsWorker().then(() => undefined);
  }

  createFirstPageRender(
    pdfBlob: Blob,
    canvas: HTMLCanvasElement,
  ): QrPosterPdfPreviewRender {
    let loadingTask: PDFDocumentLoadingTask | null = null;
    let pdfDocument: PDFDocumentProxy | null = null;
    let renderTask: RenderTask | null = null;
    let disposed = false;

    const dispose = (): void => {
      if (disposed) {
        return;
      }

      disposed = true;
      renderTask?.cancel();
      this.destroyPdfDocument(pdfDocument, loadingTask);
      canvas.width = 0;
      canvas.height = 0;
    };

    const completed = (async (): Promise<void> => {
      if (!pdfBlob.size || (pdfBlob.type && pdfBlob.type !== 'application/pdf')) {
        throw new Error('The QR poster preview PDF is invalid');
      }

      const workerPromise = this.getOrCreatePdfJsWorker();
      // A disposed preview intentionally does not cancel shared worker startup.
      // This avoids an unhandled rejection when it finishes before the worker does.
      void workerPromise.catch(() => undefined);
      const pdfjsPromise = this.getPdfJsModule();
      const pdfDataPromise = pdfBlob.arrayBuffer().then((pdfBuffer) => new Uint8Array(pdfBuffer));
      const [pdfjs, worker, pdfData] = await Promise.all([
        pdfjsPromise,
        workerPromise,
        pdfDataPromise,
      ]);
      if (disposed) {
        return;
      }

      // In PDF.js 4.10.38, getDocument only assigns task._worker when it creates
      // the worker itself. Passing this service-owned worker preserves it when
      // the per-document PDFDocumentProxy/LoadingTask is later destroyed.
      loadingTask = pdfjs.getDocument({ data: pdfData, worker });
      pdfDocument = await loadingTask.promise;

      if (disposed) {
        return;
      }
      if (pdfDocument.numPages < 1) {
        throw new Error('The QR poster preview PDF has no pages');
      }

      const page = await pdfDocument.getPage(1);
      if (disposed) {
        return;
      }

      const baseViewport = page.getViewport({ scale: 1 });
      const cssWidth = this.getCanvasDisplayWidth(canvas, baseViewport.width);
      const devicePixelRatio = Math.min(
        Math.max(window.devicePixelRatio || 1, 1),
        MAX_DEVICE_PIXEL_RATIO,
      );
      const effectiveScale = (cssWidth / baseViewport.width) * devicePixelRatio;
      const viewport = page.getViewport({
        scale: effectiveScale,
      });
      if (viewport.width <= 0 || viewport.height <= 0) {
        throw new Error('The QR poster preview canvas has invalid dimensions');
      }
      const context = canvas.getContext('2d', { alpha: false });
      if (!context) {
        throw new Error('Unable to create the QR poster preview canvas');
      }

      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      renderTask = page.render({
        canvasContext: context,
        viewport,
      });
      await renderTask.promise;
    })();

    return { completed, dispose };
  }

  private getOrCreatePdfJsWorker(): Promise<PDFWorker> {
    if (this.pdfJsWorker && !this.pdfJsWorker.destroyed) {
      return Promise.resolve(this.pdfJsWorker);
    }

    if (this.pdfJsWorker?.destroyed) {
      this.pdfJsWorker = null;
      this.pdfJsWorkerPromise = null;
    }

    if (!this.pdfJsWorkerPromise) {
      let workerPromise: Promise<PDFWorker>;
      let createdWorker: PDFWorker | null = null;
      workerPromise = this.getPdfJsModule()
        .then(async (pdfjs) => {
          const worker = new pdfjs.PDFWorker();
          createdWorker = worker;
          await worker.promise;
          if (worker.destroyed) {
            throw new Error('The QR poster PDF.js worker was destroyed during initialization');
          }
          this.pdfJsWorker = worker;
          return worker;
        })
        .catch((error) => {
          if (createdWorker && !createdWorker.destroyed) {
            createdWorker.destroy();
          }
          if (this.pdfJsWorkerPromise === workerPromise) {
            this.pdfJsWorkerPromise = null;
            this.pdfJsWorker = null;
          }
          throw error;
        });
      this.pdfJsWorkerPromise = workerPromise;
    }

    return this.pdfJsWorkerPromise;
  }

  private async getPdfJsModule(): Promise<PdfJsModule> {
    if (!this.pdfJsModulePromise) {
      let modulePromise: Promise<PdfJsModule>;
      modulePromise = import('pdfjs-dist')
        .then((pdfjs) => {
          pdfjs.GlobalWorkerOptions.workerSrc = this.getPdfJsWorkerSrc();
          return pdfjs;
        })
        .catch((error) => {
          if (this.pdfJsModulePromise === modulePromise) {
            this.pdfJsModulePromise = null;
          }
          throw error;
        });
      this.pdfJsModulePromise = modulePromise;
    }

    return this.pdfJsModulePromise;
  }

  private getPdfJsWorkerSrc(baseUri: string = document.baseURI): string {
    return new URL('assets/pdfjs/pdf.worker.min.mjs', baseUri).toString();
  }

  private getCanvasDisplayWidth(canvas: HTMLCanvasElement, pageWidth: number): number {
    const previewWidth = canvas.parentElement?.clientWidth || canvas.clientWidth;
    return Math.max(Math.floor(previewWidth || pageWidth), 1);
  }

  private destroyPdfDocument(
    pdfDocument: PDFDocumentProxy | null,
    loadingTask: PDFDocumentLoadingTask | null,
  ): void {
    const destroyPromise = pdfDocument
      ? pdfDocument.destroy()
      : loadingTask?.destroy();
    void destroyPromise?.catch(() => undefined);
  }
}
